import React, { useMemo } from 'react'
// @ts-ignore — react-apexcharts ships without bundled types in this repo
import ReactApexChart from 'react-apexcharts'
import { ChartSpec, ChartType } from './chartSpec'
import { validateChartSpec } from './validateChartSpec'

interface ChartRendererProps {
  /** Untrusted spec straight from the tool-call arguments. */
  spec: ChartSpec | Record<string, unknown>
  /** Chat theme; drives ApexCharts light/dark mode. */
  isDark?: boolean
}

/** Chart types ApexCharts renders from a flat `number[]` series (pie/donut). */
const PIE_LIKE = new Set<ChartType>(['pie', 'donut'])
/** Chart types that use a cartesian x-axis with categories. */
const CARTESIAN = new Set<ChartType>(['line', 'bar', 'area', 'scatter', 'heatmap', 'radar'])

/**
 * M&G brand palette, ordered for good contrast between adjacent series.
 * Sourced from the design tokens in `tokens-light.css`; this hardcoded list is
 * the fallback used when CSS variables can't be resolved (tests / SSR).
 */
const BRAND_PALETTE_FALLBACK = [
  '#055a60', // --color-primary (M&G green)
  '#eb5c37', // --color-secondary (orange)
  '#1bb3b7', // --color-plugin (teal)
  '#ebc334', // --color-warning (amber)
  '#3b82f6', // --color-info (blue)
  '#4caf50', // --color-success (green)
  '#7ea9ac', // --color-primary-muted
  '#3ab795' // --enterprise-agent
]

/** CSS tokens mapped 1:1 to `BRAND_PALETTE_FALLBACK`. */
const BRAND_TOKENS = [
  '--color-primary',
  '--color-secondary',
  '--color-plugin',
  '--color-warning',
  '--color-info',
  '--color-success',
  '--color-primary-muted',
  '--enterprise-agent'
]

/** Resolve the M&G brand palette from CSS tokens, falling back to fixed hex. */
function getBrandPalette(): string[] {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return BRAND_PALETTE_FALLBACK
  }
  const styles = getComputedStyle(document.documentElement)
  return BRAND_TOKENS.map((token, i) => {
    const value = styles.getPropertyValue(token).trim()
    return value || BRAND_PALETTE_FALLBACK[i]
  })
}

/** Height bounds (px). Taller default than a stock chart; scales with data volume. */
const MIN_CHART_HEIGHT = 380
const MAX_CHART_HEIGHT = 680
const HEIGHT_PER_POINT = 22
/** Height cap on small screens so the chart stays usable on mobile. */
const MOBILE_MAX_HEIGHT = 320
/**
 * Width bounds (px). The chart is centered and only grows with data volume, so
 * sparse charts stay compact rather than stretching across the full bubble.
 */
const MIN_CHART_WIDTH = 320
const MAX_CHART_WIDTH = 900
const WIDTH_PER_POINT = 110
const WIDTH_BASE = 140
/**
 * Bar charts with more categories than this render as horizontal bars, which
 * stay readable where crowded vertical bars would overlap their labels.
 */
const BAR_ORIENTATION_THRESHOLD = 8

/**
 * Compute a data-driven chart height: taller charts for more data points
 * (categories / slices), clamped so tiny charts aren't cramped and large ones
 * don't dominate the viewport.
 */
function computeChartHeight(spec: ChartSpec): number {
  const points = spec.series.reduce((max, s) => Math.max(max, s.data.length), 0)
  const scaled = MIN_CHART_HEIGHT + points * HEIGHT_PER_POINT
  return Math.min(MAX_CHART_HEIGHT, Math.max(MIN_CHART_HEIGHT, scaled))
}

/** Map a validated `ChartSpec` to ApexCharts `{ series, options, height }`. */
function toApex(spec: ChartSpec, isDark: boolean) {
  const isPieLike = PIE_LIKE.has(spec.type)
  const height = computeChartHeight(spec)

  // Number of categories/points, used to decide bar orientation.
  const pointCount = Math.max(
    spec.categories?.length ?? 0,
    spec.series.reduce((max, s) => Math.max(max, s.data.length), 0)
  )
  // Cap the rendered width by data volume so sparse charts stay compact/centered.
  const maxWidth = Math.min(
    MAX_CHART_WIDTH,
    Math.max(MIN_CHART_WIDTH, WIDTH_BASE + pointCount * WIDTH_PER_POINT)
  )
  // Auto-flip crowded bar charts to horizontal; an explicit options.horizontal wins.
  const horizontalHint = (spec.options as { horizontal?: unknown } | undefined)?.horizontal
  const isHorizontalBar =
    spec.type === 'bar' &&
    (typeof horizontalHint === 'boolean' ? horizontalHint : pointCount > BAR_ORIENTATION_THRESHOLD)

  // Pie/donut take a flat number[]; everything else takes [{ name, data }].
  const series = isPieLike
    ? spec.series[0]?.data ?? []
    : spec.series.map((s) => ({ name: s.name, data: s.data }))

  const options: Record<string, unknown> = {
    chart: {
      type: spec.type,
      stacked: spec.stacked ?? false,
      // Keep only the download tool; the zoom/pan/reset icons overlap the
      // title in the narrow chat bubble and add little value here.
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      background: 'transparent',
      height
    },
    // Shrink the chart on small screens (width is already fluid at 100%).
    responsive: [
      {
        breakpoint: 640,
        options: { chart: { height: Math.min(height, MOBILE_MAX_HEIGHT) } }
      }
    ],
    colors: getBrandPalette(),
    theme: { mode: isDark ? 'dark' : 'light' },
    // Apex's default per-bar labels overlap badly with many categories; opt-in via options.dataLabels.
    dataLabels: { enabled: false },
    ...(spec.type === 'bar'
      ? {
          plotOptions: {
            bar: {
              horizontal: isHorizontalBar,
              borderRadius: 4,
              columnWidth: '50%',
              barHeight: '55%'
            }
          }
        }
      : {}),
    ...(spec.title ? { title: { text: spec.title, align: 'left' } } : {}),
    ...(spec.subtitle ? { subtitle: { text: spec.subtitle, align: 'left' } } : {}),
    ...(isPieLike && spec.categories ? { labels: spec.categories } : {}),
    ...(CARTESIAN.has(spec.type)
      ? {
          xaxis: {
            categories: spec.categories ?? [],
            ...(spec.xAxisLabel ? { title: { text: spec.xAxisLabel } } : {})
          }
        }
      : {}),
    ...(spec.yAxisLabel ? { yaxis: { title: { text: spec.yAxisLabel } } } : {}),
    // Whitelisted styling passthrough (already sanitised by validateChartSpec).
    ...(spec.options ?? {})
  }

  return { series, options, height, maxWidth }
}

const ChartError: React.FC<{ message: string }> = ({ message }) => (
  <div
    data-testid="chart-error"
    className="w-full my-2 p-3 rounded-md border border-[var(--border-muted)] text-body text-theme-subheading">
    Unable to render chart: {message}
  </div>
)

/**
 * Renders an interactive ApexCharts chart from a (possibly untrusted) chart
 * spec. Validation runs before render; invalid specs fall back to a visible,
 * non-throwing error so the chat bubble stays alive.
 */
const ChartRenderer: React.FC<ChartRendererProps> = ({ spec, isDark = false }) => {
  const validated = useMemo(() => validateChartSpec(spec), [spec])

  if (!validated.ok) {
    return <ChartError message={validated.error} />
  }

  const { series, options, height, maxWidth } = toApex(validated.spec, isDark)

  return (
    <div className="w-full my-2 flex justify-center" data-testid="chart-renderer">
      <div className="w-full" style={{ maxWidth }}>
        <ReactApexChart
          type={validated.spec.type}
          series={series}
          options={options}
          height={height}
          width="100%"
        />
      </div>
    </div>
  )
}

export default ChartRenderer
